import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Home, Video, ClipboardList, Users, Heart, MessageCircle, Search as SearchIcon, Plus, Sparkles, Clock, Play, Lock, Layers } from 'lucide-react';
import WORLD_MAP_PATHS from './worldMapPaths';
import { metaTrack } from './utils/metaPixel';
import { logEvent } from './dashboard/utils/analytics';

// Lazy-load Mux Player so visitors who never reach the platform-overview
// section don't pay for the player chunk.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const PLATFORM_OVERVIEW_PLAYBACK_ID = 'eLAI2C7Um3nIOXo004tS01rURAeT00Rsvlhnr3vp2y67f8';

/* ─── Theme System ─── */

const THEMES = {
  dark: {
    gold: '#E2DD9F',
    goldHover: '#F5F0B8',
    goldDim: 'rgba(226,221,159,0.10)',
    goldBorder: 'rgba(226,221,159,0.25)',
    bg: '#0A0A0A',
    card: '#111111',
    elevated: '#151515',
    text: '#F5F5F5',
    textSec: '#9CA3AF',
    textDim: 'rgba(156,163,175,0.7)',
    textFaint: 'rgba(156,163,175,0.6)',
    textGhost: 'rgba(156,163,175,0.45)',
    border: 'rgba(255,255,255,0.06)',
    heroOverlay: 'linear-gradient(180deg, rgba(10,10,10,0.65) 0%, rgba(10,10,10,0.85) 60%, #0A0A0A 100%)',
    imgOverlay: 'linear-gradient(180deg, rgba(10,10,10,0.05) 0%, rgba(10,10,10,0.25) 100%)',
    navBg: 'rgba(10,10,10,0.92)',
    stickyBg: 'rgba(10,10,10,0.95)',
    ctaBtnText: '#0A0A0A',
    shadow: 'rgba(0,0,0,0.4)',
    shadowLight: 'rgba(0,0,0,0.2)',
    notBg: 'rgba(255,255,255,0.02)',
    notBorder: 'rgba(255,255,255,0.04)',
    xColor: 'rgba(255,100,100,0.6)',
    bookGrad: 'linear-gradient(160deg, #1a1a1a 0%, #111 50%, #0d0d0d 100%)',
    selectionBg: '#E2DD9F',
    selectionText: '#0A0A0A',
  },
  light: {
    gold: '#8B7B2A',
    goldHover: '#6B5E1A',
    goldDim: 'rgba(139,123,42,0.08)',
    goldBorder: 'rgba(139,123,42,0.20)',
    bg: '#FAFAF8',
    card: '#FFFFFF',
    elevated: '#F5F5F0',
    text: '#1A1A1A',
    textSec: '#5A5A5A',
    textDim: 'rgba(90,90,90,0.7)',
    textFaint: 'rgba(90,90,90,0.6)',
    textGhost: 'rgba(90,90,90,0.45)',
    border: 'rgba(0,0,0,0.08)',
    heroOverlay: 'linear-gradient(180deg, rgba(250,250,248,0.4) 0%, rgba(250,250,248,0.75) 60%, #FAFAF8 100%)',
    imgOverlay: 'linear-gradient(180deg, rgba(250,250,248,0.05) 0%, rgba(250,250,248,0.15) 100%)',
    navBg: 'rgba(250,250,248,0.92)',
    stickyBg: 'rgba(250,250,248,0.95)',
    ctaBtnText: '#FFFFFF',
    shadow: 'rgba(0,0,0,0.12)',
    shadowLight: 'rgba(0,0,0,0.06)',
    notBg: 'rgba(0,0,0,0.02)',
    notBorder: 'rgba(0,0,0,0.06)',
    xColor: 'rgba(200,60,60,0.6)',
    bookGrad: 'linear-gradient(160deg, #f8f8f5 0%, #fff 50%, #f5f5f0 100%)',
    selectionBg: '#8B7B2A',
    selectionText: '#FFFFFF',
  },
};

const TRUSTED_BY = ['NBA', 'Euroleague', 'Luka Doncic Foundation', 'NCAA D1 Coaches', 'FIBA', 'Professional Academies Worldwide'];

const PLATFORM_URL = '#/platform';

// InitiateCheckout on every "get access" click.
//
// Nothing sat between PageView and Purchase, so the funnel was a pair
// of numbers with a fortnight of silence in between and no way to tell
// a page that does not convert from a checkout that does not complete.
//
// A fresh event_id per click, because two clicks are two intents and
// should count twice - the id is here so a future server-side
// InitiateCheckout can dedupe against this one, not to collapse them.
//
// Note this fires on the click through to signup, which is earlier
// than Meta's own reading of "initiated checkout" (reaching payment).
// It is the step worth measuring on this page; if the signal turns out
// too noisy for optimisation, the place to move it is the Stripe
// redirect in Paywall.
function trackInitiateCheckout(source) {
  const id = `ic_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  metaTrack("InitiateCheckout", { content_name: "Platform subscription", content_category: source, value: 34.99, currency: "USD" }, id);
  try { logEvent("initiate_checkout", { source }); } catch {}
}
const CTA_URL = '#/signup';

/* Inline SVG icons for included cards */
const ICONS = {
  playbook: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/><path d="M6 8h3M6 12h3"/></svg>`,
  lightbulb: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>`,
  basketball: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93c4.08 2.38 8.58 2.38 14.14 0"/><path d="M4.93 19.07c4.08-2.38 8.58-2.38 14.14 0"/><path d="M12 2v20"/><path d="M2 12h20"/></svg>`,
  clipboard: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M9 12h6"/><path d="M9 16h6"/></svg>`,
  bolt: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
  film: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><path d="M7 2v20"/><path d="M17 2v20"/><path d="M2 7h5"/><path d="M2 12h20"/><path d="M2 17h5"/><path d="M17 7h5"/><path d="M17 17h5"/></svg>`,
  calendar: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/></svg>`,
  mortarboard: (c) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/></svg>`,
};
function CardIcon({ name, color, size = 28 }) {
  const fn = ICONS[name];
  if (!fn) return null;
  return <img src={`data:image/svg+xml,${encodeURIComponent(fn(color))}`} alt="" width={size} height={size} />;
}

const INCLUDED_CARDS = [
  { title: 'Player Development Insights', desc: 'Short-form ideas focused on skill acquisition, decision-making, and adaptability.', outcome: 'Walk into your next session with a fresh perspective you can apply immediately.', icon: 'lightbulb' },
  { title: 'Player Development Drills & Games', desc: 'A growing library of hundreds of drills and games, ranging from beginners to NBA-level athletes.', outcome: 'Stop Googling drills. Find ones that actually transfer to the game.', icon: 'basketball' },
  { title: 'Team Coaching Insights', desc: 'Conceptual nuggets for team coaches: spacing, constraints, teaching methods, and practice structure.', outcome: 'Make your practices sharper without overhauling your entire system.', icon: 'clipboard' },
  { title: 'Team Coaching Drills & Games', desc: 'Game-representative drills built for real practices, not just workouts.', outcome: 'Run practices where every rep looks and feels like a real game situation.', icon: 'bolt' },
  { title: "X's & O's Breakdowns", desc: 'Film-based breakdowns of actions, concepts, and trends from different levels of the game.', outcome: 'See what top programs are running and understand the why behind it.', icon: 'film' },
  { title: 'Set of the Day and your Playbook', desc: 'One set or action every day from the pro and international game, with a note on what to watch for. Save the ones you would run and keep your own notes on each.', outcome: 'Over a season it becomes a playbook built from things you have actually seen work.', icon: 'playbook' },
  { title: 'Practice Plans', desc: 'Done-for-you examples (e.g. 12U, high school, etc.) showing how to structure weeks and months.', outcome: 'Save hours of planning with proven templates you can adapt to your level.', icon: 'calendar' },
  { title: 'Masterclasses', desc: 'Guest coaches breaking down how they actually teach and build their environments.', outcome: 'Learn how other coaches solve the same problems you face every day.', icon: 'mortarboard' },
];

const APPROACHES = [
  'Modern skill acquisition research',
  'Ecological dynamics & the constraints-led approach',
  'Game-like and decision-based training',
  'Traditional drills and teaching methods',
  'Practical solutions coaches actually use in real gyms',
];

const METHOD_TILES = [
  { Icon: Layers, title: 'Foundations', desc: 'The vocabulary underneath everything else. One concept per card, in plain language, so a new idea never stalls on a word you do not know yet.' },
  { Icon: Clock, title: 'Learning Lab', desc: 'A short daily session in three beats: watch one thing, work a lane, answer one challenge. Fifteen minutes with a coffee, and what you write stays on the drill.' },
  { Icon: MessageCircle, title: 'Ask BAM', desc: 'A coaching assistant on every page. Ask it a question in plain language and it answers from the same method the certification teaches. Still in training, so check it against the library.' },
];

const NOT_LIST = [
  'A random drill dump with no context',
  'A motivational guru platform',
  'A rigid system you must follow',
  'A replacement for your coaching experience',
];

const FAQS = [
  { q: 'What exactly do I get access to?', a: 'Full access to the entire platform: hundreds of drills and games, short-form coaching insights for both player development and team coaching, film breakdowns, a new set every day and the playbook you build from them, practice plan examples, masterclasses, and the private coaching community, all for one monthly subscription.' },
  { q: 'How often is new content added?', a: 'The platform is updated weekly. New drills, insights, and breakdowns are added on a rolling basis as our coaching network continues to refine and test ideas in live environments.' },
  { q: 'Is this the same as the BAM Coaches\u2019 Certification?', a: 'No. The Certification is a longer, theory-heavy deep dive into skill acquisition and coaching methodology. The Coaches Platform is a living, growing resource designed to give you practical ideas you can use today. Some coaches use both, but they stand on their own.' },
  { q: 'Can I cancel anytime?', a: 'Yes. There are no contracts or commitments. You can cancel your subscription at any time and retain access through the end of your billing period.' },
  { q: 'Who creates the content?', a: 'Content is created and curated by Coleman Ayers and the network of 15+ BAM coaches who are actively working with players and teams every day across youth, high school, college, and professional levels.' },
  { q: 'Is this for team coaches or trainers?', a: 'Both. The platform includes dedicated sections for player development (skill trainers, individual coaches) and team coaching (practice planning, spacing, offensive/defensive concepts). Many coaches wear both hats.' },
];

/* ─── Reusable Components ─── */

function FadeIn({ children, delay = 0, style = {} }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let obs;
    const show = () => { setVisible(true); cleanup(); };
    const check = () => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 1.15) show();
    };
    const cleanup = () => {
      window.removeEventListener('scroll', check);
      if (obs) obs.disconnect();
    };
    obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) show(); },
      { threshold: 0.1, rootMargin: '0px 0px 60px 0px' }
    );
    obs.observe(el);
    window.addEventListener('scroll', check, { passive: true });
    check();
    return cleanup;
  }, []);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

function CTAButton({ large, style, t, source = 'cta' }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ textAlign: 'center', ...style }}>
      <a
        href={CTA_URL}
        onClick={() => trackInitiateCheckout(source)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'inline-block',
          padding: large ? '18px 48px' : '15px 40px',
          background: hovered ? t.goldHover : t.gold,
          color: t.ctaBtnText,
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: large ? 20 : 17,
          fontWeight: 700,
          letterSpacing: 1.5,
          textDecoration: 'none',
          borderRadius: 6,
          transition: 'all 0.25s ease',
          transform: hovered ? 'translateY(-1px)' : 'none',
          boxShadow: hovered ? `0 8px 30px ${t.goldDim}` : `0 4px 20px ${t.goldDim}`,
        }}
      >
        GET ACCESS TO THE PLATFORM
      </a>
      <div style={{ marginTop: 10, fontSize: 13, color: t.textSec, fontFamily: "'DM Sans', sans-serif" }}>
        $34.99/month &bull; Cancel anytime
      </div>
    </div>
  );
}

function SectionDivider({ t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0' }}>
      <div style={{ width: 60, height: 1, background: `linear-gradient(90deg, transparent, ${t.goldBorder})` }} />
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: t.gold, margin: '0 16px', opacity: 0.4 }} />
      <div style={{ width: 60, height: 1, background: `linear-gradient(90deg, ${t.goldBorder}, transparent)` }} />
    </div>
  );
}

function IncludedCard({ card, t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? t.elevated : t.card,
        border: `1px solid ${hovered ? t.goldBorder : t.border}`,
        borderRadius: 10,
        padding: '28px 24px',
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-3px)' : 'none',
        boxShadow: hovered ? `0 12px 40px ${t.shadow}` : `0 2px 10px ${t.shadowLight}`,
      }}
    >
      <div style={{ marginBottom: 14 }}><CardIcon name={card.icon} color={t.gold} /></div>
      <div style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: 18,
        letterSpacing: 0.8,
        color: t.text,
        marginBottom: 8,
      }}>{card.title}</div>
      <div style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14,
        lineHeight: 1.6,
        color: t.textSec,
        marginBottom: card.outcome ? 10 : 0,
      }}>{card.desc}</div>
      {card.outcome && <div style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        lineHeight: 1.5,
        color: t.gold,
        opacity: 0.7,
        fontStyle: 'italic',
      }}>{card.outcome}</div>}
    </div>
  );
}

function ImageRow({ images, height = 280, t }) {
  // Each entry can be a string (uses default position) OR an object
  // { src, position } so individual images can be tuned per-row.
  const items = images.map(it => typeof it === 'string' ? { src: it, position: 'center 20%' } : it);
  return (
    <FadeIn>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        gap: 12,
        margin: '40px 0',
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        {items.map((item, i) => (
          <div key={i} style={{
            height,
            borderRadius: 10,
            overflow: 'hidden',
            position: 'relative',
          }}>
            <img
              src={item.src}
              alt=""
              loading="lazy"
              decoding="async"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: item.position,
                display: 'block',
              }}
            />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: t.imgOverlay,
            }} />
          </div>
        ))}
      </div>
    </FadeIn>
  );
}

function FAQItem({ q, a, t, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => setOpen(!open)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered || open ? t.elevated : t.card,
        border: `1px solid ${open ? t.goldBorder : t.border}`,
        borderRadius: 10,
        padding: '20px 24px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        userSelect: 'none',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}>
        <span style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 15,
          fontWeight: 600,
          color: open ? t.text : t.textSec,
          transition: 'color 0.2s',
        }}>{q}</span>
        <span style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 22,
          color: t.gold,
          opacity: 0.6,
          transition: 'transform 0.3s ease',
          transform: open ? 'rotate(45deg)' : 'rotate(0)',
          flexShrink: 0,
        }}>+</span>
      </div>
      <div style={{
        maxHeight: open ? 300 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.4s ease, opacity 0.3s ease',
        opacity: open ? 1 : 0,
      }}>
        <p style={{
          fontSize: 14,
          lineHeight: 1.7,
          color: t.textSec,
          marginTop: 14,
          paddingTop: 14,
          borderTop: `1px solid ${t.border}`,
        }}>{a}</p>
      </div>
    </div>
  );
}

function BookCover({ src, isMobile, t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // Narrow + tall aspect crops out the source images' wide black
        // background and isolates the book itself in the frame.
        width: isMobile ? 95 : 120,
        height: isMobile ? 175 : 230,
        borderRadius: 6,
        overflow: 'hidden',
        flexShrink: 0,
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered
          ? `8px 8px 30px ${t.shadow}, 0 0 20px ${t.goldDim}`
          : `4px 4px 20px ${t.shadow}`,
        cursor: 'default',
      }}
    >
      <img src={src} alt="" loading="lazy" decoding="async"
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
    </div>
  );
}

/* Scrambled approach items that settle into place on scroll */
const SCRAMBLE_OFFSETS = [
  { x: -140, y: 35, r: -12 },
  { x: 100, y: -45, r: 15 },
  { x: -80, y: 55, r: -7 },
  { x: 160, y: -25, r: 10 },
  { x: -110, y: -40, r: -14 },
];

function ScrambledApproaches({ approaches, t }) {
  const [settled, setSettled] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSettled(true); obs.disconnect(); } },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}>
      {approaches.map((a, i) => {
        const off = SCRAMBLE_OFFSETS[i];
        return (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            opacity: settled ? 1 : 0.3,
            transform: settled
              ? 'translate(0, 0) rotate(0deg)'
              : `translate(${off.x}px, ${off.y}px) rotate(${off.r}deg)`,
            transition: `all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.15 + i * 0.12}s`,
          }}>
            <div style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: t.gold,
              marginTop: 8,
              flexShrink: 0,
              opacity: settled ? 1 : 0,
              transition: `opacity 0.4s ease ${0.4 + i * 0.12}s`,
            }} />
            <span style={{ fontSize: 15, lineHeight: 1.6, color: t.textSec }}>{a}</span>
          </div>
        );
      })}
    </div>
  );
}

/* Coach selector data: tab label, quote from their perspective, BAM's answer */
const COACH_PROFILES = [
  {
    label: 'Skills Trainers',
    quote: "I\u2019m a skills trainer looking for game-representative drills and modern player development ideas.",
    answer: "You\u2019ll find hundreds of drills ranging from beginner to NBA-level, plus short-form insights on skill acquisition, decision-making, and adaptability.",
  },
  {
    label: 'Team Coaches',
    quote: "I coach a team and need practical ideas for spacing, practice structure, and offensive concepts.",
    answer: "You\u2019ll find team coaching insights, game-representative practice drills, X\u2019s & O\u2019s breakdowns, and done-for-you practice plans from 12U through college.",
  },
  {
    label: 'New to Skill Acquisition',
    quote: "I keep hearing about ecological dynamics and constraints-led coaching, but I don\u2019t know where to start.",
    answer: "You\u2019ll find practical explanations and real-world applications, no jargon walls. Just ideas you can try in your next session.",
  },
  {
    label: 'Advanced Coaches',
    quote: "I\u2019m already deep into modern training methods and want to keep sharpening my approach.",
    answer: "You\u2019ll find nuanced breakdowns, guest masterclasses, and a community of coaches pushing the same conversations, not beginner content repackaged.",
  },
  {
    label: 'Parents & Players',
    quote: "I want to understand what good development looks like so I can make better decisions for my kid or my own training.",
    answer: "You\u2019ll find the same resources the coaches use: drills, concepts, and insights that show you what purposeful training actually looks like.",
  },
];

function CoachSelector({ t, isMobile }) {
  const [active, setActive] = useState(0);
  const profile = COACH_PROFILES[active];
  return (
    <div>
      {/* Pill tabs */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        marginBottom: 28,
      }}>
        {COACH_PROFILES.map((p, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            style={{
              padding: isMobile ? '9px 14px' : '10px 20px',
              background: i === active ? t.gold : 'transparent',
              color: i === active ? t.ctaBtnText : t.textSec,
              border: `1px solid ${i === active ? t.gold : t.border}`,
              borderRadius: 100,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: isMobile ? 12 : 13,
              fontWeight: i === active ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              letterSpacing: 0.2,
              whiteSpace: 'nowrap',
            }}
          >{p.label}</button>
        ))}
      </div>

      {/* Conversational callout */}
      <div
        key={active}
        style={{
          background: t.card,
          border: `1px solid ${t.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          animation: 'fadeIn 0.35s ease',
        }}
      >
        {/* Their quote */}
        <div style={{
          padding: isMobile ? '24px 20px 20px' : '28px 36px 24px',
          borderLeft: `3px solid ${t.goldBorder}`,
          marginLeft: isMobile ? 16 : 28,
        }}>
          <p style={{
            fontSize: isMobile ? 15 : 17,
            fontStyle: 'italic',
            lineHeight: 1.7,
            color: t.textSec,
          }}>
            &ldquo;{profile.quote}&rdquo;
          </p>
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: t.border,
          margin: `0 ${isMobile ? '20px' : '36px'}`,
        }} />

        {/* BAM's answer */}
        <div style={{
          padding: isMobile ? '20px 20px 24px' : '24px 36px 28px',
          borderLeft: `3px solid ${t.gold}`,
          marginLeft: isMobile ? 16 : 28,
        }}>
          <p style={{
            fontSize: isMobile ? 15 : 17,
            lineHeight: 1.7,
            color: t.text,
            fontWeight: 500,
          }}>
            {profile.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

/* Glow dot locations on the 672x315 map - approximate BAM presence worldwide */
const GLOW_DOTS_672 = [
  [130,80],[160,90],      // US East/West
  [110,120],              // Mexico
  [150,200],              // Brazil
  [310,60],[330,55],[340,65],[320,75], // Europe
  [360,140],              // East Africa
  [430,80],[480,95],      // Middle East, India
  [540,90],[560,95],      // SE Asia, China
  [590,220],              // Australia
  [338,78],               // Italy / Balkans
  [325,48],               // Nordics
];

function WorldMap({ scrollY, t, isMobile }) {
  const pulse = Math.sin(scrollY * 0.006) * 0.3 + 0.7;
  const allPaths = Object.values(WORLD_MAP_PATHS).flat();
  return (
    <svg viewBox="0 0 672 315" style={{ width: '100%', maxWidth: isMobile ? 400 : 700, height: 'auto', display: 'block', margin: '0 auto' }}>
      {/* Continent outlines */}
      {allPaths.map((d, i) => (
        <path key={i} d={d} fill={t.gold} opacity="0.1" />
      ))}
      {/* Glowing BAM location dots */}
      {GLOW_DOTS_672.map(([x, y], i) => (
        <g key={`g${i}`}>
          <circle cx={x} cy={y} r="14" fill={t.gold} opacity={0.04 * pulse} />
          <circle cx={x} cy={y} r="7" fill={t.gold} opacity={0.12 * pulse} />
          <circle cx={x} cy={y} r="3" fill={t.gold} opacity={0.45 + pulse * 0.35} />
        </g>
      ))}
    </svg>
  );
}

/* Mini sidebar section navigator */
const NAV_SECTIONS = [
  { id: 'hero', label: 'Top' },
  { id: 'included', label: 'Included' },
  { id: 'approach', label: 'Approach' },
  { id: 'who', label: 'Who' },
  { id: 'community', label: 'Community' },
  { id: 'team', label: 'Team' },
  { id: 'faq', label: 'FAQ' },
  { id: 'pricing', label: 'Pricing' },
];

function SidebarNav({ scrollY, t }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const visible = scrollY > 600;
  return (
    <div style={{
      position: 'fixed',
      right: 20,
      top: '50%',
      transform: visible ? 'translateY(-50%)' : 'translateY(-50%) translateX(80px)',
      opacity: visible ? 1 : 0,
      transition: 'all 0.4s ease',
      zIndex: 98,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 0,
    }}>
      {/* Toggle dot */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: t.card,
            border: `1.5px solid ${t.goldBorder}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 24px ${t.shadow}, 0 0 12px ${t.goldDim}`,
            transition: 'all 0.2s ease',
            animation: 'sidebarPulse 2.5s ease-in-out infinite',
          }}
        >
          <div style={{
            width: 4, height: 4, borderRadius: '50%', background: t.gold,
            boxShadow: `0 -8px 0 ${t.gold}, 0 8px 0 ${t.gold}`,
          }} />
        </button>
      )}

      {/* Expanded nav */}
      {open && (
        <div style={{
          background: t.card,
          border: `1px solid ${t.border}`,
          borderRadius: 10,
          padding: '10px 6px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          boxShadow: `0 8px 30px ${t.shadow}`,
          animation: 'fadeIn 0.2s ease',
          position: 'relative',
        }}>
          <button
            onClick={() => setOpen(false)}
            style={{
              position: 'absolute',
              top: 4,
              right: 6,
              background: 'none',
              border: 'none',
              color: t.textSec,
              cursor: 'pointer',
              fontSize: 14,
              fontFamily: "'Bebas Neue', sans-serif",
              opacity: 0.5,
              padding: '2px 4px',
              lineHeight: 1,
            }}
          >&times;</button>
          {NAV_SECTIONS.map((s, i) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={() => setOpen(false)}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                padding: '6px 14px 6px 10px',
                fontSize: 11,
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: 1.2,
                color: hovered === i ? t.gold : t.textSec,
                textDecoration: 'none',
                borderRadius: 6,
                background: hovered === i ? t.goldDim : 'transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >{s.label}</a>
          ))}
        </div>
      )}
    </div>
  );
}


function ThemeToggle({ isDark, onToggle, t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        background: hovered ? t.goldDim : 'transparent',
        border: `1px solid ${t.goldBorder}`,
        borderRadius: 5,
        padding: '8px 10px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.25s ease',
        color: t.gold,
        fontSize: 16,
        lineHeight: 1,
      }}
    >
      {isDark ? '\u2600\uFE0F' : '\u{1F319}'}
    </button>
  );
}

/* ─── Main Page ─── */

export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mode, setMode] = useState('dark');
  const [testimonials, setTestimonials] = useState([]);
  // Founding 50 - first 50 platform subscribers get 15% off forever.
  // Source of truth: Stripe coupon's max_redemptions/times_redeemed.
  const [founding, setFounding] = useState(null); // null until loaded; { remaining, total, claimed, valid }

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // Load testimonials from Supabase
    (async () => {
      try {
        const { supabase } = await import('./supabaseClient');
        const { data } = await supabase
          .from('testimonials')
          .select('*')
          .eq('published', true)
          .order('display_order', { ascending: true });
        if (data && data.length > 0) setTestimonials(data);
      } catch {}
    })();
    // Founding 50 spots - fetch + soft-poll every 60s while page is open
    // so the counter ticks down in near-real-time during launch hour.
    const loadFounding = async () => {
      try {
        const r = await fetch('/api/founding-spots', { cache: 'no-store' });
        if (r.ok) setFounding(await r.json());
      } catch {}
    };
    loadFounding();
    const foundingPoll = setInterval(loadFounding, 60_000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      clearInterval(foundingPoll);
    };
  }, []);

  const t = THEMES[mode];
  const isDark = mode === 'dark';
  const toggleTheme = () => setMode(m => m === 'dark' ? 'light' : 'dark');
  const logo = '/images/microscope.svg';

  return (
    <div style={{
      background: t.bg,
      color: t.text,
      fontFamily: "'DM Sans', sans-serif",
      minHeight: '100vh',
      overflowX: 'hidden',
      paddingBottom: 0,
      transition: 'background 0.4s ease, color 0.4s ease',
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body { background: ${t.bg}; transition: background 0.4s ease; }
        ::selection { background: ${t.selectionBg}; color: ${t.selectionText}; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sidebarPulse { 0%, 100% { box-shadow: 0 4px 24px rgba(0,0,0,0.3), 0 0 0 0 rgba(226,221,159,0.3); } 50% { box-shadow: 0 4px 24px rgba(0,0,0,0.3), 0 0 0 8px rgba(226,221,159,0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '16px 0',
        background: scrollY > 50 ? t.navBg : 'transparent',
        backdropFilter: scrollY > 50 ? 'blur(20px)' : 'none',
        borderBottom: scrollY > 50 ? `1px solid ${t.border}` : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: `0 ${isMobile ? '20px' : '40px'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logo} alt="BAM" style={{ height: 28 }} />
            <span style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 18,
              letterSpacing: 3,
              color: t.gold,
            }}>BAM</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 16 }}>
            {!isMobile && ['Included', 'Approach', 'Community', 'Pricing'].map(label => (
              <a
                key={label}
                href={`#${label.toLowerCase()}`}
                onClick={(e) => { e.preventDefault(); const el = document.getElementById(label.toLowerCase()); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  color: t.textSec,
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  letterSpacing: 0.3,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => e.target.style.color = t.gold}
                onMouseLeave={e => e.target.style.color = t.textSec}
              >{label}</a>
            ))}
            <ThemeToggle isDark={isDark} onToggle={toggleTheme} t={t} />
            <a
              href={CTA_URL}
              onClick={() => trackInitiateCheckout('nav')}
              style={{
                padding: '10px 24px',
                background: 'transparent',
                border: `1px solid ${t.goldBorder}`,
                color: t.gold,
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 14,
                letterSpacing: 1.2,
                textDecoration: 'none',
                borderRadius: 5,
                transition: 'all 0.25s ease',
              }}
              onMouseEnter={e => { e.target.style.background = t.gold; e.target.style.color = t.ctaBtnText; }}
              onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = t.gold; }}
            >
              GET ACCESS
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="hero" style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/landing/hero.jpg"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
            transform: `scale(${1 + scrollY * 0.0003})`,
            transition: 'transform 0.1s linear',
          }}
        >
          <source src="/landing/hero-video.mp4" type="video/mp4" />
        </video>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: t.heroOverlay,
        }} />
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          maxWidth: 800,
          padding: `140px ${isMobile ? '24px' : '40px'} 100px`,
          animation: 'fadeUp 0.8s ease-out',
        }}>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 13,
            letterSpacing: 3,
            color: t.gold,
            marginBottom: 20,
            textTransform: 'uppercase',
          }}>By Any Means Basketball</div>
          <h1 style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: isMobile ? 48 : 72,
            lineHeight: 1,
            letterSpacing: 2,
            color: t.text,
            marginBottom: 20,
            textShadow: isDark
              ? '0 4px 30px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)'
              : '0 4px 30px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)',
          }}>
            The BAM Coaches<br />Platform
          </h1>
          <p style={{
            fontSize: isMobile ? 16 : 19,
            fontStyle: 'italic',
            color: t.textSec,
            lineHeight: 1.6,
            marginBottom: 12,
            fontWeight: 400,
          }}>
            Practical tools, drills, and conversations for coaches who want to keep getting better.
          </p>
          <p style={{
            fontSize: isMobile ? 14 : 16,
            color: t.textDim,
            lineHeight: 1.7,
            maxWidth: 640,
            margin: '0 auto 44px',
          }}>
            Built for skills trainers, team coaches, and anyone serious about player development. Hundreds of drills, short-form insights, practice plans, and a global coaching community, updated weekly.
          </p>
          <CTAButton large t={t} source="hero" />
        </div>
      </section>

      {/* TRUSTED BY */}
      <FadeIn style={{
        padding: `48px ${isMobile ? '24px' : '40px'} 16px`,
        textAlign: 'center',
      }}>
        <p style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 12,
          letterSpacing: 3,
          color: t.textSec,
          textTransform: 'uppercase',
          marginBottom: 24,
          opacity: 0.6,
        }}>Trusted by coaches &amp; organizations at</p>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? 16 : 32,
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {TRUSTED_BY.map((org, i) => (
            <span key={i} style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 16 : 20,
              letterSpacing: 2,
              color: t.textSec,
              opacity: 0.4,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.target.style.opacity = '0.8'}
            onMouseLeave={e => e.target.style.opacity = '0.4'}
            >{org}</span>
          ))}
        </div>
      </FadeIn>

      {/* VIDEO PLACEHOLDER */}
      <FadeIn style={{ maxWidth: 900, margin: '0 auto', padding: `44px ${isMobile ? '24px' : '40px'}` }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h3 style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 15,
            letterSpacing: 2.5,
            color: t.gold,
            textTransform: 'uppercase',
          }}>Platform Overview</h3>
        </div>
        <div style={{
          position: 'relative',
          width: '100%',
          paddingBottom: '56.25%',
          background: `linear-gradient(135deg, ${t.card} 0%, ${t.elevated} 100%)`,
          border: `1px solid ${t.border}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <Suspense fallback={
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                width: 36, height: 36,
                border: `3px solid ${t.goldDim}`,
                borderTop: `3px solid ${t.gold}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}/>
            </div>
          }>
            <MuxPlayer
              playbackId={PLATFORM_OVERVIEW_PLAYBACK_ID}
              streamType="on-demand"
              accentColor={t.gold}
              metadata={{ video_title: 'Platform Overview' }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
          </Suspense>
        </div>
      </FadeIn>

      {/* INTRO / WHAT YOU'LL FIND */}
      <FadeIn style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: `40px ${isMobile ? '24px' : '40px'} 80px`,
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: isMobile ? 16 : 18,
          lineHeight: 1.8,
          color: t.textSec,
          maxWidth: 720,
          margin: '0 auto 40px',
        }}>
          Rather than a longer, theory-heavy course like our Coaches' Certification, we designed this to be a <span style={{ color: t.text, fontWeight: 600 }}>living, growing platform</span> designed to give coaches practical ideas they can actually use, today.
        </p>

        <h3 style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 15,
          letterSpacing: 2.5,
          color: t.gold,
          marginBottom: 28,
          textTransform: 'uppercase',
        }}>You'll Find</h3>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          justifyContent: 'center',
          marginBottom: 20,
        }}>
          {['Short, consumable insights', 'Hundreds of drills and games', 'Save your own drills to a personal library', 'Team and player development resources', 'Ongoing additions and updates', 'A community to ask questions and exchange ideas'].map((item, i) => (
            <div key={i} style={{
              padding: '10px 20px',
              background: t.goldDim,
              border: `1px solid ${t.goldBorder}`,
              borderRadius: 100,
              fontSize: 13,
              fontWeight: 600,
              color: t.gold,
              letterSpacing: 0.3,
            }}>{item}</div>
          ))}
        </div>
      </FadeIn>

      <ImageRow images={['/landing/pro-dev.jpg', '/landing/film-review.jpg', '/landing/coaching-1.jpg']} t={t} />

      <SectionDivider t={t} />

      {/* WHAT'S INCLUDED */}
      <section id="included" style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: `20px ${isMobile ? '24px' : '40px'} 40px`,
      }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 50 }}>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 36 : 48,
              letterSpacing: 2,
              color: t.text,
              marginBottom: 10,
            }}>What's Included</h2>
            <p style={{ fontSize: 15, color: t.textSec }}>Everything you need to keep growing as a coach.</p>
          </div>
        </FadeIn>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
        }}>
          {INCLUDED_CARDS.map((card, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <IncludedCard card={card} t={t} />
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.2}>
          <p style={{
            textAlign: 'center',
            marginTop: 32,
            fontSize: 13,
            fontStyle: 'italic',
            color: t.textFaint,
          }}>
            The platform is updated weekly as new drills, insights, and breakdowns are added.
          </p>
        </FadeIn>
      </section>

      <SectionDivider t={t} />

      {/* OUR APPROACH */}
      <section id="approach" style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: `20px ${isMobile ? '24px' : '40px'}`,
      }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 36 : 48,
              letterSpacing: 2,
              color: t.text,
              marginBottom: 14,
            }}>Our Approach</h2>
            <p style={{
              fontSize: isMobile ? 15 : 17,
              lineHeight: 1.8,
              color: t.textSec,
              maxWidth: 620,
              margin: '0 auto',
            }}>
              We don't pick sides. The best coaching pulls from <span style={{ color: t.text, fontWeight: 600 }}>every method that works</span>, and that's exactly how this platform is built.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            justifyContent: 'center',
            marginBottom: 28,
          }}>
            <ScrambledApproaches approaches={APPROACHES} t={t} />
          </div>
        </FadeIn>

        <FadeIn delay={0.15}>
          <p style={{ textAlign: 'center', fontSize: 14, lineHeight: 1.7, color: t.textFaint, maxWidth: 560, margin: '0 auto' }}>
            Always through a practical basketball lens, not theory for theory's sake.
          </p>
        </FadeIn>

        {/* The method, built in: the three surfaces that carry the
            approach above into the platform itself. */}
        <FadeIn delay={0.2}>
          <div style={{ textAlign: 'center', margin: '44px 0 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: t.gold, opacity: 0.85 }}>The method, built in</div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 14,
          }}>
            {METHOD_TILES.map((m, i) => (
              <div key={i} style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: 12,
                padding: '20px 20px 22px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <m.Icon size={16} color={t.gold} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{m.title}</div>
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.65, color: t.textSec }}>{m.desc}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <ImageRow
        images={[
          { src: '/landing/coaching-summer.jpg', position: 'center 38%' }, // faces sit a third of the way down
          { src: '/landing/coaching-2.jpg', position: 'center 28%' },      // face at the top quarter
        ]}
        height={320}
        t={t}
      />

      <SectionDivider t={t} />

      {/* WHO THIS IS FOR */}
      <section id="who" style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: `20px ${isMobile ? '24px' : '40px'}`,
      }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 36 : 48,
              letterSpacing: 2,
              color: t.text,
              marginBottom: 12,
            }}>Who This is For</h2>
            <p style={{ fontSize: 15, color: t.textSec }}>Select your role to see what you'll find inside.</p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <CoachSelector t={t} isMobile={isMobile} />
        </FadeIn>

        <FadeIn delay={0.2}>
          <p style={{
            textAlign: 'center',
            fontSize: 15,
            color: t.textSec,
            lineHeight: 1.7,
            marginTop: 32,
          }}>
            If you're involved in developing players or teams and curious enough to keep learning, <span style={{ color: t.text, fontWeight: 600 }}>you'll fit in</span>.
          </p>
        </FadeIn>
      </section>

      <SectionDivider t={t} />

      {/* GLOBAL COMMUNITY */}
      <section id="community" style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: `0 0 20px`,
      }}>
        {/* Cinematic hero block: photo bg + map overlay + heading */}
        <FadeIn>
          <div style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: isMobile ? 0 : 16,
            margin: isMobile ? 0 : `0 ${isMobile ? '0' : '40px'}`,
          }}>
            {/* Low-opacity coaching photo */}
            <img
              src="/landing/global-horizontal.jpg"
              alt=""
              loading="lazy"
              decoding="async"
              style={{
                width: '100%',
                height: isMobile ? 380 : 480,
                objectFit: 'cover',
                objectPosition: 'center 25%',
                display: 'block',
                opacity: isDark ? 0.15 : 0.1,
              }}
            />

            {/* Full overlay for contrast */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: isDark
                ? 'radial-gradient(ellipse at center, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.9) 70%)'
                : 'radial-gradient(ellipse at center, rgba(250,250,248,0.4) 0%, rgba(250,250,248,0.85) 70%)',
            }} />

            {/* Map + content centered on top */}
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {/* Map with glow dots */}
              <div style={{ position: 'relative', marginBottom: isMobile ? -20 : -30 }}>
                <WorldMap scrollY={scrollY} t={t} isMobile={isMobile} />
              </div>

              {/* Heading overlaid below map */}
              <h2 style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: isMobile ? 36 : 56,
                letterSpacing: 3,
                color: t.text,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2,
                textShadow: isDark ? '0 2px 20px rgba(0,0,0,0.5)' : 'none',
              }}>A Global Community</h2>
              <p style={{
                fontSize: isMobile ? 14 : 16,
                color: t.textSec,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2,
                maxWidth: 420,
                lineHeight: 1.6,
              }}>Coaches from <span style={{ color: t.gold, fontWeight: 600 }}>35+ countries</span> connected by a shared commitment to getting better.</p>
            </div>
          </div>
        </FadeIn>

        {/* Community details below */}
        <FadeIn delay={0.1}>
          <div style={{
            maxWidth: 800,
            margin: '0 auto',
            padding: `40px ${isMobile ? '24px' : '40px'} 0`,
          }}>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: t.textSec, marginBottom: 24 }}>
              Beyond the content, you also get access to a private community where coaches:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {['Ask questions', 'Share ideas and drills', 'Discuss training challenges', 'Learn how others are applying concepts in real environments'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: t.gold, flexShrink: 0, opacity: 0.6 }} />
                  <span style={{ fontSize: 15, color: t.textSec }}>{item}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: t.textSec, marginBottom: 12 }}>
              There are also <span style={{ color: t.text, fontWeight: 600 }}>regular group calls</span> where coaches can jump in, listen, and discuss topics around player and team development.
            </p>
          </div>
        </FadeIn>
      </section>

      <SectionDivider t={t} />

      {/* BUILT FROM REAL COACHING ENVIRONMENTS */}
      <section id="team" style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: `20px ${isMobile ? '24px' : '40px'}`,
      }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 36 : 48,
              letterSpacing: 2,
              color: t.text,
              marginBottom: 16,
            }}>Built From Real<br />Coaching Environments</h2>
          </div>
        </FadeIn>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          gap: 20,
          marginBottom: 40,
        }}>
          {[
            { num: '15+', label: 'BAM Coaches' },
            { num: '500+', label: 'Players Each Year' },
            { num: '35+', label: 'Countries Worldwide' },
          ].map((stat, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div style={{
                textAlign: 'center',
                padding: '32px 20px',
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: 10,
              }}>
                <div style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 42,
                  color: t.gold,
                  letterSpacing: 1,
                  lineHeight: 1,
                  marginBottom: 8,
                }}>{stat.num}</div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: t.textSec,
                  textTransform: 'uppercase',
                }}>{stat.label}</div>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn>
          <div style={{
            fontSize: 15,
            lineHeight: 1.85,
            color: t.textSec,
            textAlign: 'center',
            maxWidth: 720,
            margin: '0 auto',
          }}>
            <p style={{ marginBottom: 20 }}>
              The BAM Coaches Platform is shaped by the ongoing work of a network of 15+ By Any Means coaches, collectively working with 500+ players each year across youth, high school, collegiate, and professional levels.
            </p>
            <p style={{ marginBottom: 20 }}>
              Our coaches aren't just contributors. They're actively testing, refining, and applying ideas in gyms every day. The drills, concepts, and discussions inside the platform come directly from what's being used, adjusted, and stress-tested in live settings.
            </p>
          </div>
        </FadeIn>

        {/* Coleman bio */}
        <FadeIn delay={0.1}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '200px 1fr',
            gap: isMobile ? 24 : 36,
            alignItems: 'start',
            padding: isMobile ? '32px 24px' : '40px 44px',
            background: t.card,
            border: `1px solid ${t.border}`,
            borderRadius: 12,
            marginTop: 40,
          }}>
            <div style={{
              width: isMobile ? 140 : '100%',
              aspectRatio: '1',
              borderRadius: 10,
              overflow: 'hidden',
              margin: isMobile ? '0 auto' : 0,
            }}>
              <img src="/landing/coleman.jpg" alt="Coleman Ayers" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 13,
                letterSpacing: 2,
                color: t.gold,
                marginBottom: 6,
                textTransform: 'uppercase',
              }}>Founder</div>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 24,
                letterSpacing: 1,
                color: t.text,
                marginBottom: 14,
              }}>Coleman Ayers</div>
              <p style={{ fontSize: 14, lineHeight: 1.8, color: t.textSec, marginBottom: 12 }}>
                Over the past decade, Coleman has trained athletes from youth to professional levels, consulted with coaching staffs ranging from high school programs to college and NBA environments, and worked alongside coaches across 35+ countries worldwide.
              </p>
              <p style={{ fontSize: 14, lineHeight: 1.8, color: t.textSec }}>
                Coleman has authored two books on player development and modern basketball coaching, both of which reached #1 in the Basketball Coaching category on Amazon. Those ideas don't live on paper alone. They're continuously challenged, refined, and applied through the coaches and environments connected to By Any Means.
              </p>
            </div>
          </div>
        </FadeIn>

        {/* BOOKS */}
        <FadeIn delay={0.15}>
          <div style={{
            display: 'flex',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: isMobile ? 20 : 40,
            marginTop: 40,
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <div style={{
              display: 'flex',
              gap: isMobile ? 16 : 20,
              flexShrink: 0,
            }}>
              <BookCover src="/modern%20basketball%20blueprint%20coaches%20platform.png" isMobile={isMobile} t={t} />
              <BookCover src="/deep%20skill%20coaches%20platform.png" isMobile={isMobile} t={t} />
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: isMobile ? 'center' : 'left',
            }}>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 13,
                letterSpacing: 2,
                color: t.gold,
                textTransform: 'uppercase',
                marginBottom: 8,
              }}>#1 Amazon Bestsellers</div>
              <p style={{
                fontSize: 14,
                lineHeight: 1.7,
                color: t.textSec,
              }}>
                Two books that reached #1 in the Basketball Coaching category. The ideas inside are continuously tested and refined through the BAM coaching network.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      <ImageRow
        images={[
          // All three are tall portraits in a short wide box, so the crop
          // point is what decides whether a face is in frame. Each is set
          // so the band starts just above the heads.
          { src: '/landing/other-coach.jpg', position: 'center 12%' }, // coach pointing, face at the top fifth
          { src: '/landing/pro-dev-2.jpg', position: 'center 10%' },   // two players, faces at the top quarter
          { src: '/landing/coaching-3.jpg', position: 'center 50%' },  // huddle, faces mid-frame under the ceiling
        ]}
        height={320}
        t={t}
      />

      <SectionDivider t={t} />

      {/* THIS ISN'T... */}
      <section style={{
        maxWidth: 700,
        margin: '0 auto',
        padding: `20px ${isMobile ? '24px' : '40px'}`,
        textAlign: 'center',
      }}>
        <FadeIn>
          <h2 style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: isMobile ? 32 : 40,
            letterSpacing: 2,
            color: t.text,
            marginBottom: 32,
          }}>This Isn't...</h2>
        </FadeIn>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32, alignItems: 'center' }}>
          {NOT_LIST.map((item, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 24px',
                background: t.notBg,
                border: `1px solid ${t.notBorder}`,
                borderRadius: 8,
                width: '100%',
                maxWidth: 460,
              }}>
                <span style={{ color: t.xColor, fontSize: 16, flexShrink: 0 }}>&#10005;</span>
                <span style={{ fontSize: 14, color: t.textSec }}>{item}</span>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn>
          <p style={{
            fontSize: 16,
            lineHeight: 1.8,
            color: t.textSec,
            maxWidth: 560,
            margin: '0 auto',
          }}>
            It's a resource to <span style={{ color: t.text, fontWeight: 600 }}>expand your toolbox</span>, sharpen your thinking, and stay connected to how the game is actually being taught today.
          </p>
        </FadeIn>
      </section>

      <SectionDivider t={t} />

      {/* FAQ */}
      <section id="faq" style={{
        maxWidth: 740,
        margin: '0 auto',
        padding: `20px ${isMobile ? '24px' : '40px'}`,
      }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 36 : 48,
              letterSpacing: 2,
              color: t.text,
              marginBottom: 10,
            }}>Questions?</h2>
          </div>
        </FadeIn>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQS.map((faq, i) => (
            <FadeIn key={i} delay={i * 0.04}>
              <FAQItem q={faq.q} a={faq.a} t={t} defaultOpen={i === 0} />
            </FadeIn>
          ))}
        </div>

        {/* Post-FAQ nudge */}
        <FadeIn delay={0.2}>
          <div style={{
            textAlign: 'center',
            marginTop: 40,
            padding: '28px 0',
            borderTop: `1px solid ${t.border}`,
          }}>
            <p style={{
              fontSize: 15,
              color: t.textSec,
              marginBottom: 16,
            }}>Still here? That probably means it's for you.</p>
            <a
              href={CTA_URL}
              onClick={() => trackInitiateCheckout('nav')}
              style={{
                display: 'inline-block',
                padding: '14px 36px',
                background: t.gold,
                color: t.ctaBtnText,
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: 1.2,
                textDecoration: 'none',
                borderRadius: 6,
                transition: 'all 0.25s ease',
              }}
              onMouseEnter={e => { e.target.style.background = t.goldHover; }}
              onMouseLeave={e => { e.target.style.background = t.gold; }}
            >GET ACCESS</a>
          </div>
        </FadeIn>
      </section>

      <SectionDivider t={t} />

      {/* FINAL CTA */}
      <FadeIn>
        <section id="pricing" style={{
          padding: `80px ${isMobile ? '24px' : '40px'} 60px`,
          textAlign: 'center',
        }}>
          <h2 style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: isMobile ? 36 : 52,
            letterSpacing: 2,
            color: t.text,
            marginBottom: 10,
          }}>Full Access to the<br />BAM Coaches Platform</h2>

          {/* Founding 50 callout - only when valid + spots remain */}
          {founding?.valid && founding.remaining > 0 && (
            <FoundingCallout founding={founding} t={t} isMobile={isMobile}/>
          )}

          <p style={{
            fontSize: 28,
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: 1,
            color: t.gold,
            marginBottom: founding?.valid && founding.remaining > 0 ? 6 : 32,
          }}>
            {founding?.valid && founding.remaining > 0 ? (
              <>
                <span style={{textDecoration: 'line-through', color: t.textDim, fontSize: 22, marginRight: 10}}>$34.99/mo</span>
                $29.74/month
              </>
            ) : (
              "$34.99/month"
            )}
          </p>
          {founding?.valid && founding.remaining > 0 && (
            <p style={{
              fontSize: 12,
              color: t.textDim,
              letterSpacing: .5,
              marginBottom: 32,
            }}>
              Founding pricing locked for life · standard $34.99 after the first 50
            </p>
          )}
          <CTAButton large t={t} source="footer" />

          <div style={{ marginTop: 24 }}>
            <a
              href={PLATFORM_URL}
              style={{
                fontSize: 14,
                color: t.textSec,
                textDecoration: 'none',
                borderBottom: `1px solid ${t.goldBorder}`,
                paddingBottom: 2,
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => e.target.style.color = t.text}
              onMouseLeave={e => e.target.style.color = t.textSec}
            >
              Explore the Platform
            </a>
          </div>
        </section>
      </FadeIn>

      {/* TESTIMONIALS - auto-hidden until real ones exist in Supabase.
          Add rows to the `testimonials` table and the section reappears
          with them; no code change needed. */}
      {testimonials.length > 0 && (
        <FadeIn delay={0.1}>
          <section style={{
            padding: `${isMobile ? '60px 24px' : '80px 40px'}`,
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, color: t.gold, letterSpacing: 2.5,
              textTransform: 'uppercase', marginBottom: 14,
            }}>
              What coaches are saying
            </div>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: isMobile ? 28 : 36,
              color: t.text, letterSpacing: 2, marginBottom: 40,
            }}>
              BUILT BY COACHES, FOR COACHES
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 20, maxWidth: 1000, margin: '0 auto',
            }}>
              {testimonials.map((t2, i) => (
                <div key={i} style={{
                  background: t.cardBg || 'rgba(255,255,255,0.03)',
                  border: `1px solid ${t.border}`,
                  borderRadius: 14, padding: 24, textAlign: 'left',
                }}>
                  <div style={{ fontSize: 14, color: t.textSec, lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic' }}>
                    "{t2.quote}"
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{t2.name}</div>
                  <div style={{ fontSize: 11, color: t.textGhost }}>{t2.role}</div>
                </div>
              ))}
            </div>
          </section>
        </FadeIn>
      )}

      {/* FOOTER */}
      <footer style={{
        borderTop: `1px solid ${t.border}`,
        padding: `40px ${isMobile ? '24px' : '40px'}`,
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: 13,
          color: t.textGhost,
          maxWidth: 560,
          margin: '0 auto 20px',
          lineHeight: 1.7,
        }}>
          The BAM Coaches Platform stands on its own. Some coaches later choose to pursue deeper education through certification. Others simply use the platform as an ongoing resource.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <img src={logo} alt="BAM" style={{ height: 18, opacity: 0.3 }} />
          <span style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 13,
            letterSpacing: 2,
            color: t.gold,
            opacity: 0.3,
          }}>BAM</span>
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobile ? 14 : 22,
          marginTop: 18,
          fontSize: 11,
          color: t.textGhost,
        }}>
          <a href="#/privacy" style={{ color: t.textGhost, textDecoration: 'none', opacity: 0.7 }}>Privacy Policy</a>
          <a href="#/terms" style={{ color: t.textGhost, textDecoration: 'none', opacity: 0.7 }}>Terms of Service</a>
          <a href="#/refund" style={{ color: t.textGhost, textDecoration: 'none', opacity: 0.7 }}>Refund Policy</a>
          <a href="#/support" style={{ color: t.textGhost, textDecoration: 'none', opacity: 0.7 }}>Support</a>
        </div>
        <p style={{
          fontSize: 11,
          color: t.textGhost,
          marginTop: 16,
          opacity: 0.6,
        }}>
          &copy; {new Date().getFullYear()} By Any Means Basketball. All rights reserved.
        </p>
      </footer>

      {/* SIDEBAR NAV */}
      {!isMobile && <SidebarNav scrollY={scrollY} t={t} />}
    </div>
  );
}

// Founding Members callout - pill + headline + progress bar.
// Renders only when there's still a spot. Auto-applies at checkout via
// the create-checkout-session endpoint, no code for the buyer to enter.
function FoundingCallout({ founding, t, isMobile }) {
  const { remaining, total, claimed } = founding;
  const pct = Math.max(2, Math.round((claimed / total) * 100));
  const urgent = remaining <= 10;
  return (
    <div style={{
      maxWidth: 520,
      margin: '0 auto 22px',
      padding: '16px 22px',
      background: urgent ? 'rgba(248,113,113,0.08)' : 'rgba(226,221,159,0.08)',
      border: `1px solid ${urgent ? 'rgba(248,113,113,0.40)' : t.goldBorder}`,
      borderRadius: 14,
      textAlign: 'center',
    }}>
      <div style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: 999,
        background: urgent ? 'rgba(248,113,113,0.18)' : 'rgba(226,221,159,0.18)',
        border: `1px solid ${urgent ? 'rgba(248,113,113,0.45)' : t.goldBorder}`,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        color: urgent ? '#F87171' : t.gold,
        marginBottom: 8,
      }}>
        {urgent ? '⚡ Going fast' : 'Founding Members · 15% off for life'}
      </div>
      <p style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: isMobile ? 22 : 26,
        letterSpacing: 1.4,
        color: t.text,
        margin: '0 0 10px',
        textTransform: 'uppercase',
        lineHeight: 1.1,
      }}>
        {remaining} of {total} founding spots remaining
      </p>
      <div style={{
        height: 6,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.08)',
        overflow: 'hidden',
        margin: '0 auto',
        maxWidth: 360,
      }}>
        <div style={{
          height: 6,
          width: `${pct}%`,
          background: urgent
            ? 'linear-gradient(90deg, #F87171 0%, #ef4444 100%)'
            : `linear-gradient(90deg, ${t.gold} 0%, #d4ce8a 100%)`,
          transition: 'width .4s',
        }}/>
      </div>
      <p style={{
        fontSize: 11,
        color: t.textDim,
        marginTop: 10,
        marginBottom: 0,
      }}>
        First 50 sign-ups lock in 15% off forever · auto-applied at checkout
      </p>
    </div>
  );
}
